
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getDoc, getRelated, getHistory } from '@/services/api';
import React, { useState } from 'react';

const formatDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return '—';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return isoDate;
  }
};

const getStatusInfo = (status: string | null | undefined) => {
  const s = (status || '').toLowerCase().trim();
  if (s === 'active' || s === 'опубликован' || s === 'действует' || s === 'published') {
    return { label: 'Действует', cssClass: 'active' };
  }
  if (s === 'amended' || s === 'изменён') {
    return { label: 'Изменён', cssClass: 'amended' };
  }
  if (s === 'repealed' || s === 'утратил силу' || s === 'отменён' || s === 'revoked') {
    return { label: 'Утратил силу', cssClass: 'repealed' };
  }
  if (s === 'project' || s === 'проект' || s === 'draft') {
    return { label: 'Проект', cssClass: 'project' };
  }
  return { label: status || 'Неизвестно', cssClass: 'unknown' };
};

// Скачивание текста документа в Word (.doc)
const downloadWord = (title: string, text: string, eoNumber: string) => {
  const html = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset='utf-8'><title>${title}</title></head>
    <body>
      <h1>${title}</h1>
      <p><strong>№ EO:</strong> ${eoNumber}</p>
      <hr/>
      <pre style="white-space: pre-wrap; font-family: 'Times New Roman', serif; font-size: 14px;">${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
    </body></html>
  `;
  const blob = new Blob(['\\ufeff', html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${eoNumber}.doc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const DocumentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'text' | 'pdf' | 'related' | 'history'>('text');

  const { data: docData, isLoading: docLoading, error: docError } = useQuery(
    ['doc', id], () => getDoc(id!), { enabled: !!id }
  );
  const { data: relData } = useQuery(
    ['related', id], () => getRelated(id!), { enabled: !!id }
  );
  const { data: histData } = useQuery(
    ['history', id], () => getHistory(id!), { enabled: !!id }
  );

  const doc = docData;
  const related = relData?.data || [];
  const history = histData?.data || [];

  // PDF URL напрямую с publication.pravo.gov.ru (скрытый)
  const pdfUrl = doc?.pdf_url || '';

  if (docLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Загрузка...</span>
        </div>
      </div>
    );
  }

  if (docError || !doc) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Документ не найден или ошибка загрузки
      </div>
    );
  }

  const statusInfo = getStatusInfo(doc.status);

  const renderText = (text: string) => {
    if (!text) return <p className="text-muted fst-italic">Текст документа недоступен</p>;
    return (
      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: '15px' }}>
        {text}
      </div>
    );
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <h4 className="fw-bold mb-2">{doc.title}</h4>
              <div className="d-flex flex-wrap gap-3 text-muted small">
                <span className="font-monospace fw-medium">{doc.eo_number}</span>
                <span>•</span>
                <span>{doc.authority || '—'}</span>
                <span>•</span>
                <span>{formatDate(doc.doc_date || doc.view_date)}</span>
                <span>•</span>
                <span className={`badge-status badge-${statusInfo.cssClass}`}>
                  {statusInfo.label}
                </span>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-outline-warning btn-sm"><i className="bi bi-star me-1"></i>В избранное</button>
              <button className="btn btn-outline-primary btn-sm"><i className="bi bi-share me-1"></i>Поделиться</button>
              {pdfUrl && (
                <>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setActiveTab('pdf')}
                  >
                    <i className="bi bi-file-earmark-pdf me-1"></i>PDF
                  </button>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-secondary btn-sm"
                    title="Скачать PDF"
                  >
                    <i className="bi bi-download me-1"></i>PDF
                  </a>
                </>
              )}
              <button
                className="btn btn-outline-success btn-sm"
                onClick={() => downloadWord(doc.title, doc.full_text || '', doc.eo_number)}
                disabled={!doc.full_text}
              >
                <i className="bi bi-file-earmark-word me-1"></i>Скачать Word
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white p-0">
              <ul className="nav nav-tabs card-header-tabs" style={{ padding: '0 1rem' }}>
                <li className="nav-item">
                  <button className={`nav-link${activeTab === 'text' ? ' active' : ''}`} onClick={() => setActiveTab('text')}>
                    <i className="bi bi-file-text me-1"></i>Текст
                  </button>
                </li>
                {pdfUrl && (
                  <li className="nav-item">
                    <button className={`nav-link${activeTab === 'pdf' ? ' active' : ''}`} onClick={() => setActiveTab('pdf')}>
                      <i className="bi bi-file-earmark-pdf me-1"></i>PDF
                    </button>
                  </li>
                )}
                <li className="nav-item">
                  <button className={`nav-link${activeTab === 'related' ? ' active' : ''}`} onClick={() => setActiveTab('related')}>
                    <i className="bi bi-link-45deg me-1"></i>Связанные документы
                  </button>
                </li>
                <li className="nav-item">
                  <button className={`nav-link${activeTab === 'history' ? ' active' : ''}`} onClick={() => setActiveTab('history')}>
                    <i className="bi bi-clock-history me-1"></i>История изменений
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body p-4">
              {activeTab === 'text' && renderText(doc.full_text || '')}
              {activeTab === 'pdf' && pdfUrl && (
                <div className="text-center py-5">
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-danger btn-lg">
                    <i className="bi bi-file-earmark-pdf me-2"></i>Открыть PDF
                  </a>
                </div>
              )}
              {activeTab === 'related' && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead><tr><th>Документ</th><th>Тип связи</th><th>Уверенность</th></tr></thead>
                    <tbody>
                      {related.length === 0 && <tr><td colSpan={3} className="text-muted text-center">Связанные документы не найдены</td></tr>}
                      {related.map((rel: any) => (
                        <tr key={rel.id || rel.eo_number}>
                          <td>
                            <Link to={`/document/${rel.id || rel.eo_number}`} className="text-decoration-none fw-medium">{rel.title || 'Без названия'}</Link>
                            <div className="small text-muted font-monospace">{rel.eo_number}</div>
                          </td>
                          <td><span className="badge bg-secondary">{rel.relation || '—'}</span></td>
                          <td>
                            <div className="progress" style={{ height: 6, width: 80 }}>
                              <div className="progress-bar" style={{ width: `${((rel.confidence || 0) * 100)}%` }}></div>
                            </div>
                            <small className="text-muted">{Math.round((rel.confidence || 0) * 100)}%</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === 'history' && (
                <div className="timeline">
                  {history.length === 0 && <p className="text-muted text-center">История изменений не найдена</p>}
                  {history.map((h: any, i: number) => (
                    <div key={h.id || i} className="d-flex gap-3 pb-4" style={{ borderLeft: i === history.length - 1 ? 'none' : '2px solid #e2e8f0', paddingLeft: 16, marginLeft: 7 }}>
                      <div className="rounded-circle bg-primary" style={{ width: 14, height: 14, marginLeft: -24, marginTop: 4, flexShrink: 0 }}></div>
                      <div>
                        <div className="fw-medium">{h.changes_summary || h.change_type || 'Версия ' + h.version_number}</div>
                        <div className="small text-muted">{formatDate(h.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white">
              <h6 className="fw-bold mb-0"><i className="bi bi-link-45deg me-2 text-primary"></i>Связанные документы</h6>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {related.slice(0, 5).map((rel: any) => (
                  <Link key={rel.id || rel.eo_number} to={`/document/${rel.id || rel.eo_number}`} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-medium small">{rel.title || 'Без названия'}</div>
                      <div className="text-muted" style={{ fontSize: '12px' }}>{rel.eo_number}</div>
                    </div>
                    <span className="badge bg-light text-dark" style={{ fontSize: '11px' }}>{rel.relation || '—'}</span>
                  </Link>
                ))}
                {related.length === 0 && <div className="list-group-item text-muted text-center py-3">Нет связей</div>}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white">
              <h6 className="fw-bold mb-0"><i className="bi bi-journal-text me-2 text-success"></i>Заметка</h6>
            </div>
            <div className="card-body">
              <textarea className="form-control" rows={4} placeholder="Ваша личная заметка к документу..."></textarea>
              <button className="btn btn-primary btn-sm w-100 mt-2">Сохранить заметку</button>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white">
              <h6 className="fw-bold mb-0"><i className="bi bi-info-circle me-2 text-info"></i>Метаданные</h6>
            </div>
            <div className="card-body">
              <dl className="row mb-0" style={{ fontSize: '14px' }}>
                <dt className="col-sm-5 text-muted">Тип:</dt>
                <dd className="col-sm-7">{doc.doc_type || '—'}</dd>
                <dt className="col-sm-5 text-muted">Орган:</dt>
                <dd className="col-sm-7">{doc.authority || '—'}</dd>
                <dt className="col-sm-5 text-muted">Дата:</dt>
                <dd className="col-sm-7">{formatDate(doc.doc_date || doc.view_date)}</dd>
                <dt className="col-sm-5 text-muted">Статус:</dt>
                <dd className="col-sm-7">
                  <span className={`badge-status badge-${statusInfo.cssClass}`}>
                    {statusInfo.label}
                  </span>
                </dd>
                {doc.doc_number && (
                  <>
                    <dt className="col-sm-5 text-muted">Номер:</dt>
                    <dd className="col-sm-7">{doc.doc_number}</dd>
                  </>
                )}
                {doc.jd_reg_number && (
                  <>
                    <dt className="col-sm-5 text-muted">Рег. №:</dt>
                    <dd className="col-sm-7">{doc.jd_reg_number}</dd>
                  </>
                )}
                {doc.pages_count > 0 && (
                  <>
                    <dt className="col-sm-5 text-muted">Страниц:</dt>
                    <dd className="col-sm-7">{doc.pages_count}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

